// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { useWindowSize } from "@eisinc/App/Hooks/windowResizeHook";
import { CustomFieldDefinition } from "@eisinc/CustomFieldDefinition";
import Button, { ButtonPresentationProps } from "@eisinc/MobiusOverrides/Button";
import LazyImage from "@eisinc/MobiusOverrides/LazyImage";
import { screen } from "@eisinc/XDL/Helpers";
import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { responsiveStyleRules } from "@insite/client-framework/Common/Utilities/responsive";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import parse from "html-react-parser";
import React, { FC, useEffect, useState } from "react";
import { css } from "styled-components";

import { A11y, Autoplay, Controller, EffectFade, FreeMode, Navigation, Pagination, Scrollbar } from "swiper/modules";
import { Swiper, SwiperProps, SwiperSlide } from "swiper/react";

// XNG-Change: totaly customized
interface SlideModel {
    fields: {
        slideType: "single" | "multi" | "mirror";
        slideTitle: string;
        background: "image" | "color" | "video";
        videoUrl: string;
        videoCaptionUrl: string;
        imageDesktop: string;
        imageTablet: string;
        imageMobile: string;
        imageOverlay: string;
        partialOverlay: boolean;
        partialOverlayPositioning: "top" | "middle" | "bottom";
        responsiveImageBehavior: "cover" | "center" | "prioritizeHeight" | "prioritizeWidth" | "prioritizeBoth";
        backgroundColor: string;
        heading: string;
        subheading: string;
        slideInnerImage: string;
        switchImageOrder: boolean;
        buttonLabel: string;
        buttonLink: LinkFieldValue;
        buttonVariant: "primary" | "secondary" | "tertiary";
        openInNewTabSingle: boolean;
        slideLink: LinkFieldValue;
        openInNewTab: boolean;
        focalPoint:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        centerTextVertically: boolean;
        contentPadding: number;
        imageDesktop1: string;
        imageTablet1: string;
        imageMobile1: string;
        slideLink1: LinkFieldValue;
        openInNewTab1: boolean;
        imageDesktop2: string;
        imageTablet2: string;
        imageMobile2: string;
        slideLink2: LinkFieldValue;
        openInNewTab2: boolean;
        imageDesktop3: string;
        imageTablet3: string;
        imageMobile3: string;
        slideLink3: LinkFieldValue;
        openInNewTab3: boolean;
        imageDesktop4: string;
        imageTablet4: string;
        imageMobile4: string;
        slideLink4: LinkFieldValue;
        openInNewTab4: boolean;
    };
}

const enum fields {
    height = "height",
    textAlignment = "textAlignment",
    showArrows = "showArrows",
    slideIndicator = "slideIndicator",
    autoplay = "autoplay",
    slides = "slides",
    responsiveFontSizes = "responsiveFontSizes",
    customFontSizes = "customFontSizes",
    h1FontSize = "h1FontSize",
    h2FontSize = "h2FontSize",
    h3FontSize = "h3FontSize",
    h4FontSize = "h4FontSize",
    h5FontSize = "h5FontSize",
    h6FontSize = "h6FontSize",
    normalFontSize = "normalFontSize",
    upperPadding = "upperPadding",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.height]: "1/4 viewport" | "1/2 viewport" | "3/4 viewport" | "fullViewport";
        [fields.textAlignment]: "left" | "center" | "right";
        [fields.showArrows]: boolean;
        [fields.slideIndicator]: boolean;
        [fields.autoplay]: number;
        [fields.responsiveFontSizes]: boolean;
        [fields.customFontSizes]: boolean;
        [fields.h1FontSize]: number;
        [fields.h2FontSize]: number;
        [fields.h3FontSize]: number;
        [fields.h4FontSize]: number;
        [fields.h5FontSize]: number;
        [fields.normalFontSize]: number;
        [fields.slides]: SlideModel[];
        [fields.upperPadding]: number;
    };
    extendedStyles?: SlideshowStyles;
}

export interface SlideshowStyles {
    spinnerContainer?: InjectableCss;
    carouselContainer?: InjectableCss;
    slideContainerWrapper?: InjectableCss;
    slideContentWrapper?: InjectableCss;
    slideOverlayWrapper?: InjectableCss;
    slideInnerImageWrapperStyles?: InjectableCss;
    headingText?: TypographyPresentationProps;
    subheadingText?: TypographyPresentationProps;
    slideButton?: ButtonPresentationProps;
}

export const slideshowStyles: SlideshowStyles = {
    spinnerContainer: {
        css: css`
            width: 100%;
            padding: 10px 0;
            position: relative;
            min-height: 200px;
            display: flex;
            justify-content: center;
            align-items: center;
        `,
    },
    slideContainerWrapper: {
        css: css`
            display: flex;
        `,
    },
    slideContentWrapper: {
        css: css`
            display: flex;
            flex: 0 0 100%;
            position: relative;
            color: white;
            background-repeat: no-repeat;
        `,
    },
    slideOverlayWrapper: {
        css: css`
            width: 100%;
        `,
    },
    slideButton: {
        css: css`
            width: fit-content;
        `,
    },
    slideInnerImageWrapperStyles: {
        css: css`
            width: 50%;
        `,
    },
};

const prepareBackground = (backgroundUrl?: string) => {
    return css`
        background-image: url(${backgroundUrl});
        background-size: cover;
        background-repeat: no-repeat;
        background-position: center;
    `;
};

// eslint-disable-next-line default-param-last
const imageStyle = (marginBottom = false, backgroundUrl: string) => {
    return css`
        margin-bottom: ${marginBottom ? 20 : 0}px;
        ${prepareBackground(backgroundUrl)};
        ${screen("lt", "lg")`
            padding: calc(5% + 50px);
        `}
    `;
};

const mobileImageStyle = (backgroundUrl?: string) => {
    return css`
        height: 100%;
        width: 100%;
        ${prepareBackground(backgroundUrl)};
        ${screen("lt", "lg")`
            padding: calc(5% + 50px);
        `}
    `;
};

interface ImageSlideProps {
    slide: SlideModel;
    index: number;
    links: any[];
    linkPropertyName: "slideLink1" | "slideLink2" | "slideLink3" | "slideLink4";
    newTab: boolean;
    styles?: any;
}

const ImageSlide = ({ slide, index, links, linkPropertyName, newTab, styles }: ImageSlideProps) => {
    const customerType = useAppSelector(state => getSession(state).properties?.["mpExistingCustomer"]);
    return slide.fields[linkPropertyName]?.value?.length > 0 && links[index] ? (
        <Clickable
            className="swiper-lazy"
            href={links[index]?.url}
            target={newTab ? "_blank" : "_self"}
            onClick={() =>
                (window as any)._MpSlideShowClick(index, links[index]?.url, slide.fields.imageDesktop, customerType)
            }
            {...styles}
        >
            <VisuallyHidden>{translate(`Go To ${links[index]?.url}`)}</VisuallyHidden>
        </Clickable>
    ) : (
        <StyledWrapper className="swiper-lazy" {...styles} />
    );
};

interface SingleSlideProps {
    slide: SlideModel;
    links: any[];
    index: number;
    history: any;
    styles: any;
    heightStyles: any;
    textAlignStyles: any;
    fields: any;
    slideLinks: any;
}

const StyledVideo = getStyledWrapper("video");

const SingleSlideImage = ({
    slide,
    links,
    index,
    styles,
    heightStyles,
    textAlignStyles,
    fields,
    slideLinks,
}: SingleSlideProps) => {
    const buttonLink = links[index];
    const slideLink = slideLinks[index];
    const isSlideLinkPrepared = slide.fields.slideLink?.value && !!slideLink;

    // XNG-Changes
    const customerType = useAppSelector(state => getSession(state).properties?.["mpExistingCustomer"]);

    let responsiveImageBehaviorStyles;
    if (slide.fields.responsiveImageBehavior === "cover") {
        responsiveImageBehaviorStyles = "background-size: cover;";
    } else if (slide.fields.responsiveImageBehavior === "center") {
        responsiveImageBehaviorStyles = "background-size: contain;";
    } else if (slide.fields.responsiveImageBehavior === "prioritizeHeight") {
        responsiveImageBehaviorStyles = "background-size: auto 100%;";
    } else if (slide.fields.responsiveImageBehavior === "prioritizeWidth") {
        responsiveImageBehaviorStyles = "background-size: 100% auto;";
    } else if (slide.fields.responsiveImageBehavior === "prioritizeBoth") {
        responsiveImageBehaviorStyles = "background-size: 100% 100%";
    }

    const backgroundStyles =
        slide.fields.background === "image"
            ? ({ theme }: { theme: BaseTheme }) =>
                  breakpointMediaQueries(theme, [
                      css`
                          background-image: url(${slide.fields.imageMobile || slide.fields.imageDesktop});
                      `,
                      css`
                          background-image: url(${slide.fields.imageTablet || slide.fields.imageDesktop});
                      `,
                      css`
                          background-image: url(${slide.fields.imageTablet || slide.fields.imageDesktop});
                      `,
                      css`
                          background-image: url(${slide.fields.imageDesktop});
                      `,
                      css`
                          background-image: url(${slide.fields.imageDesktop});
                      `,
                  ])
            : css`
                  background-color: ${slide.fields.backgroundColor};
              `;

    const focalPointStyles = getFocalPointStyles(slide.fields.focalPoint);

    let overlayPositioningStyles;
    if (!slide.fields.partialOverlay) {
        overlayPositioningStyles = "align-items: stretch;";
    } else if (slide.fields.partialOverlayPositioning === "top") {
        overlayPositioningStyles = "align-items: flex-start;";
    } else if (slide.fields.partialOverlayPositioning === "middle") {
        overlayPositioningStyles = "align-items: center;";
    } else if (slide.fields.partialOverlayPositioning === "bottom") {
        overlayPositioningStyles = "align-items: flex-end;";
    }

    let fontSizeStyles;
    if (fields.responsiveFontSizes || fields.customFontSizes) {
        fontSizeStyles = responsiveStyleRules(fields.responsiveFontSizes, fields.customFontSizes ? fields : undefined);
    }

    const slideWrapperStyles = {
        css: css`
            ${styles.slideContentWrapper?.css || ""};
            ${textAlignStyles};
            ${backgroundStyles};
            ${responsiveImageBehaviorStyles};
            ${focalPointStyles};
            ${overlayPositioningStyles};
            ${fontSizeStyles};
            ${isSlideLinkPrepared && `cursor: pointer;`}
            background-color: ${slide.fields.background !== "color" && slide.fields.partialOverlay
                ? slide.fields.imageOverlay
                : ""};

            ${screen("gt", "sm")`
                ${heightStyles}
            `}

            ${screen("lte", "sm")`
                height: 325px;
            `}

            ${screen("lte", "xs")`
                height: 443px;
            `}
        `,
    };

    const slideOverlayWrapperStyles = {
        css: css`
            z-index: 1;
            ${styles.slideOverlayWrapper?.css || ""}
            padding: ${slide.fields.contentPadding}px;
            ${(slide.fields.slideInnerImage || slide.fields.centerTextVertically) && `display: flex;`}
            ${slide.fields.centerTextVertically &&
            `
                align-items: center;
                justify-content: center;`}
            ${slide.fields.switchImageOrder && `flex-direction: row-reverse;`}
        `,
    };

    const videoStyle = {
        css: css`
            position: absolute;
            left: 0;
            top: 0;
            right: 0;
            bottom: 0;
            object-fit: cover;
            width: 100%;
            max-height: 100%;
            ${heightStyles}
        `,
    };
    let video: any;
    if (slide.fields.background === "video" && slide.fields.videoUrl?.length) {
        video = (
            <StyledVideo autoPlay={true} playsInline={true} muted loop {...videoStyle}>
                <source src={slide.fields.videoUrl} type="video/mp4" />
                <source src={slide.fields.videoUrl} type="video/webm" />
                <source src={slide.fields.videoUrl} type="video/ogg" />
                {/* xng-change */}
                <track kind="captions" srcLang="en" label="English" src={slide.fields.videoCaptionUrl} default />
                {translate("Your Browser Does Not Support HTML 5 Video ")}
            </StyledVideo>
        );
    }
    const slideCenteringWrapperStyles = {
        css: css`
            ${slide.fields.slideInnerImage && `width: 50%;`}
            display: flex;
            flex-direction: column;
            ${fields.textAlignment === "center"
                ? css`
                      align-items: center;
                  `
                : fields.textAlignment === "right"
                  ? css`
                        align-items: flex-end;
                    `
                  : css`
                        align-items: flex-start;
                    `}
        `,
    };

    const onClick = (link: string | undefined, openInNewTabSingle: boolean | undefined) => {
        if (link) {
            if (openInNewTabSingle) {
                window.open(link);

                return;
            }

            window.location.href = link;
        }
    };

    const handleSingleSlidClick = () => {
        if (isSlideLinkPrepared) {
            onClick(slideLink?.url, slide.fields.openInNewTab);
        }
    };

    return (
        <StyledWrapper {...slideWrapperStyles} className="swiper-lazy" onClick={handleSingleSlidClick}>
            {video}
            <StyledWrapper {...slideOverlayWrapperStyles}>
                <StyledWrapper {...slideCenteringWrapperStyles}>
                    {slide.fields.heading && (
                        <Typography {...styles.headingText}>{parse(slide.fields.heading, parserOptions)}</Typography>
                    )}
                    {slide.fields.subheading && (
                        <Typography {...styles.subheadingText}>
                            {parse(slide.fields.subheading, parserOptions)}
                        </Typography>
                    )}
                    {(slide.fields.buttonLabel || slide.fields.buttonLink?.value) && (
                        <Button
                            {...styles.slideButton}
                            variant={slide.fields.buttonVariant}
                            onClick={e => {
                                e.stopPropagation();
                                onClick(buttonLink?.url, slide.fields.openInNewTabSingle);
                                (window as any)._MpSlideShowClick(
                                    index,
                                    links[index]?.url,
                                    slide.fields.imageDesktop,
                                    customerType,
                                );
                            }}
                        >
                            {slide.fields.buttonLabel || buttonLink?.title || buttonLink?.url}
                        </Button>
                    )}
                </StyledWrapper>
                {slide.fields.slideInnerImage && (
                    <StyledWrapper {...styles.slideInnerImageWrapperStyles}>
                        <LazyImage src={slide.fields.slideInnerImage}></LazyImage>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

type Props = OwnProps & HasHistory & SwiperProps;

const Slideshow: FC<Props> = ({ fields, history, extendedStyles, ...swiperProps }) => {
    const { links: slideLinks1 } = useGetLinks(fields.slides, (slide: SlideModel) => slide.fields.slideLink1);
    const { links: slideLinks2 } = useGetLinks(fields.slides, (slide: SlideModel) => slide.fields.slideLink2);
    const { links: slideLinks3 } = useGetLinks(fields.slides, (slide: SlideModel) => slide.fields.slideLink3);
    const { links: slideLinks4 } = useGetLinks(fields.slides, (slide: SlideModel) => slide.fields.slideLink4);
    const { links: buttonLinks } = useGetLinks(fields.slides, (slide: SlideModel) => slide.fields.buttonLink);
    const { links: slideLinks } = useGetLinks(fields.slides, (slide: SlideModel) => slide.fields.slideLink);

    const windowDimensions = useWindowSize(500).width;
    const styles = useMergeStyles("slideshow", slideshowStyles, extendedStyles);
    const [swiper, setSwiper] = useState({} as any);
    const [width, setWidth] = useState(0);
    const [showSpinner, setShowSpinner] = React.useState(true);

    useEffect(() => {
        setShowSpinner(false);
    }, []);

    useEffect(() => {
        setWidth(windowDimensions);

        // eslint-disable-next-line no-prototype-builtins
        if (swiper?.hasOwnProperty("update") && width === windowDimensions) {
            swiper.update();
        }
    }, [windowDimensions]);

    const updateSwiper = (swiper: any) => {
        if (!Object.keys(swiper).length) {
            setSwiper(swiper);
        }
    };

    let heightStyles = "";
    switch (fields.height) {
        case "1/4 viewport":
            heightStyles = "min-height: 230px;";
            break;
        case "1/2 viewport":
            heightStyles = "min-height: 460px;";
            break;
        case "3/4 viewport":
            heightStyles = "min-height: 690px;";
            break;
        case "fullViewport":
            heightStyles = "min-height: 920px;";
            break;
    }

    const textAlignStyles = `text-align: ${fields.textAlignment};`;
    const carouselContainer = css`
        position: relative;
        margin-bottom: 20px;

        .swiper-carousel {
            box-sizing: border-box;
            display: flex;
            justify-content: center;
        }

        .swiper-wrapper {
            box-sizing: border-box;
        }

        .swiper-navigation {
            .swiper-button-prev-slideshow,
            .swiper-button-next-slideshow {
                position: absolute;
                top: 50%;
                color: ${getColor("common.white")};
                background: rgba(0, 0, 0, 0.4);
                cursor: pointer;
                transform: translateY(-50%);
                font-size: 24px;
                font-weight: bold;
                z-index: ${getProp("theme.zIndex.default")};
                transition: all 0.2s ease;

                &:hover,
                &:focus {
                    background: rgba(0, 0, 0, 0.8);
                }
            }

            .swiper-button-prev-slideshow {
                left: 0;
                padding: 10px 10px 8px 6px;

                &:after {
                    content: "prev";
                    font-family: "swiper-icons", sans-serif;
                }
            }

            .swiper-button-next-slideshow {
                right: 0;
                padding: 10px 6px 8px 10px;

                &:after {
                    content: "next";
                    font-family: "swiper-icons", sans-serif;
                }
            }
        }

        .swiper-pagination {
            position: static;
            margin-top: 20px;

            .swiper-pagination-bullet {
                width: 12px;
                height: 12px;
                border-radius: 50%;
                background: #d1d1d7;
                margin-right: 7px;
                opacity: 1;

                &:focus {
                    outline: none;
                }
            }

            .swiper-pagination-bullet-active {
                background: ${getColor("secondary.main")};

                &:focus {
                    outline: none;
                }
            }
        }

        .swiper-slide.mobile-slide {
            ${textAlignStyles};
        }
    `;

    const slideshowWrapper = {
        css: css`
            padding-top: ${fields.upperPadding}px;
            position: relative;
        `,
    };

    const wrapper = {
        css: css`
            display: flex;
            ${textAlignStyles};
            width: 100%;

            ${screen("gt", "lg")`
                ${heightStyles}
            `}
        `,
    };

    const smallImageWrapper = {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            width: 32%;
        `,
    };

    const prepareStyles = (slide: any) => {
        let image1Styles;
        let image2Styles;
        let image3Styles;
        let image4Styles;

        if (width >= 1025) {
            if (slide.fields.slideType === "multi") {
                image1Styles = {
                    css: css`
                        margin-right: 20px;
                        width: 68%;

                        ${screen("lt", "xl")`
                            padding: calc(11% + 50px);
                        `}

                        ${imageStyle(false, slide.fields.imageDesktop1)};
                    `,
                };
                image3Styles = {
                    css: css`
                        height: 33.3333%;
                        ${imageStyle(true, slide.fields.imageDesktop3)};
                    `,
                };
                image4Styles = {
                    css: css`
                        height: 33.3333%;
                        ${imageStyle(false, slide.fields.imageDesktop4)};
                    `,
                };
            } else {
                image1Styles = {
                    css: css`
                        height: 33.3333%;
                        ${imageStyle(true, slide.fields.imageDesktop1)};
                    `,
                };
                image3Styles = {
                    css: css`
                        height: 33.3333%;
                        ${imageStyle(false, slide.fields.imageDesktop3)};
                    `,
                };
                image4Styles = {
                    css: css`
                        width: 68%;
                        margin-left: 20px;

                        ${screen("lt", "xl")`
                            padding: calc(11% + 50px);
                        `}

                        ${imageStyle(false, slide.fields.imageDesktop4)};
                    `,
                };
            }

            image2Styles = {
                css: css`
                    height: 33.3333%;
                    ${imageStyle(true, slide.fields.imageDesktop2)};
                `,
            };
        }

        if (width < 1025 && width >= 481) {
            image1Styles = {
                css: css`
                    ${screen("lt", "xl")`
                        padding: calc(11% + 50px);
                    `}

                    ${mobileImageStyle(slide.fields.imageTablet1 || slide.fields.imageDesktop1)};
                `,
            };

            image2Styles = {
                css: css`
                    ${mobileImageStyle(slide.fields.imageTablet2 || slide.fields.imageDesktop2)};
                `,
            };

            image3Styles = {
                css: css`
                    ${mobileImageStyle(slide.fields.imageTablet3 || slide.fields.imageDesktop3)};
                `,
            };

            image4Styles = {
                css: css`
                    ${mobileImageStyle(slide.fields.imageTablet4 || slide.fields.imageDesktop4)};
                `,
            };
        }

        if (width < 481) {
            image1Styles = {
                css: css`
                    ${screen("lt", "xl")`
                        padding: calc(11% + 50px);
                    `}

                    ${mobileImageStyle(slide.fields.imageMobile1 || slide.fields.imageDesktop1)};
                `,
            };

            image2Styles = {
                css: css`
                    ${mobileImageStyle(slide.fields.imageMobile2 || slide.fields.imageDesktop2)};
                `,
            };

            image3Styles = {
                css: css`
                    ${mobileImageStyle(slide.fields.imageMobile3 || slide.fields.imageDesktop3)};
                `,
            };

            image4Styles = {
                css: css`
                    ${mobileImageStyle(slide.fields.imageMobile4 || slide.fields.imageDesktop4)};
                `,
            };
        }

        return {
            image1Styles,
            image2Styles,
            image3Styles,
            image4Styles,
        };
    };

    if (showSpinner) {
        return (
            <StyledWrapper {...styles.spinnerContainer}>
                <LoadingSpinner color="primary.main" />
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...slideshowWrapper}>
            <StyledWrapper className="carousel-slideshow-container" css={carouselContainer}>
                <SlideshowContent
                    history={history}
                    styles={styles}
                    wrapper={wrapper}
                    heightStyles={heightStyles}
                    textAlignStyles={textAlignStyles}
                    smallImageWrapper={smallImageWrapper}
                    width={width}
                    fields={fields}
                    slideLinks={slideLinks}
                    buttonLinks={buttonLinks}
                    slideLinks1={slideLinks1}
                    slideLinks2={slideLinks2}
                    slideLinks3={slideLinks3}
                    slideLinks4={slideLinks4}
                    prepareStyles={prepareStyles}
                    updateSwiper={updateSwiper}
                    swiperProps={swiperProps}
                />
            </StyledWrapper>
        </StyledWrapper>
    );
};
interface SlideshowContentProps {
    history: any;
    styles: SlideshowStyles;
    wrapper: InjectableCss;
    heightStyles: string;
    textAlignStyles: string;
    smallImageWrapper: InjectableCss;
    width: number;
    fields: any;
    slideLinks: any;
    buttonLinks: any;
    slideLinks1: any;
    slideLinks2: any;
    slideLinks3: any;
    slideLinks4: any;
    prepareStyles: (slide: SlideModel) => any;
    updateSwiper: (swiper: any) => void;
    swiperProps?: SwiperProps;
}
class SlideshowContent extends React.Component<SlideshowContentProps> {
    shouldComponentUpdate(prevProps: SlideshowContentProps) {
        // To prevent re-render while props not changed
        return !(JSON.stringify(this.props) === JSON.stringify(prevProps));
    }

    render() {
        const {
            history,
            styles,
            wrapper,
            heightStyles,
            textAlignStyles,
            smallImageWrapper,
            width,
            fields,
            slideLinks,
            buttonLinks,
            slideLinks1,
            slideLinks2,
            slideLinks3,
            slideLinks4,
            prepareStyles,
            updateSwiper,
            swiperProps,
        } = this.props;

        return (
            <>
                <Swiper
                    {...swiperProps}
                    onInit={(swiper: any) => updateSwiper(swiper)}
                    {...styles.slideContainerWrapper}
                    slidesPerView={1}
                    pagination={{ el: ".swiper-pagination", type: "bullets", clickable: true }}
                    navigation={{
                        nextEl: ".swiper-button-next-slideshow",
                        prevEl: ".swiper-button-prev-slideshow",
                    }}
                    loop={true}
                    autoplay={{ delay: fields.autoplay * 1000 }}
                    className="swiper-carousel"
                    modules={[Navigation, Pagination, Scrollbar, A11y, Controller, Autoplay, EffectFade, FreeMode]}
                >
                    {fields.slides?.map((slide: SlideModel, index: number) => {
                        if (slide.fields.slideType === "single") {
                            return (
                                <SwiperSlide key={`slide-${index}-${slide.fields.slideTitle}`}>
                                    <SingleSlideImage
                                        slide={slide}
                                        links={buttonLinks}
                                        index={index}
                                        history={history}
                                        heightStyles={heightStyles}
                                        styles={styles}
                                        textAlignStyles={textAlignStyles}
                                        fields={fields}
                                        slideLinks={slideLinks}
                                    />
                                </SwiperSlide>
                            );
                        }

                        const preparedStyles = prepareStyles(slide);

                        if (width >= 1025) {
                            if (slide.fields.slideType === "multi") {
                                return (
                                    <SwiperSlide key={`slide-${index}-${slide.fields.slideTitle}`}>
                                        <StyledWrapper {...wrapper}>
                                            <ImageSlide
                                                slide={slide}
                                                index={index}
                                                links={slideLinks1}
                                                newTab={slide.fields.openInNewTab1}
                                                linkPropertyName="slideLink1"
                                                styles={preparedStyles?.image1Styles}
                                            />
                                            <StyledWrapper {...smallImageWrapper}>
                                                <ImageSlide
                                                    slide={slide}
                                                    index={index}
                                                    links={slideLinks2}
                                                    newTab={slide.fields.openInNewTab2}
                                                    linkPropertyName="slideLink2"
                                                    styles={preparedStyles?.image2Styles}
                                                />
                                                <ImageSlide
                                                    slide={slide}
                                                    index={index}
                                                    links={slideLinks3}
                                                    newTab={slide.fields.openInNewTab3}
                                                    linkPropertyName="slideLink3"
                                                    styles={preparedStyles?.image3Styles}
                                                />
                                                <ImageSlide
                                                    slide={slide}
                                                    index={index}
                                                    links={slideLinks4}
                                                    newTab={slide.fields.openInNewTab4}
                                                    linkPropertyName="slideLink4"
                                                    styles={preparedStyles?.image4Styles}
                                                />
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    </SwiperSlide>
                                );
                            }

                            return (
                                <SwiperSlide key={`slide-${index}-${slide.fields.slideTitle}`}>
                                    <StyledWrapper {...wrapper}>
                                        <StyledWrapper {...smallImageWrapper}>
                                            <ImageSlide
                                                slide={slide}
                                                index={index}
                                                links={slideLinks1}
                                                newTab={slide.fields.openInNewTab1}
                                                linkPropertyName="slideLink1"
                                                styles={preparedStyles?.image1Styles}
                                            />
                                            <ImageSlide
                                                slide={slide}
                                                index={index}
                                                links={slideLinks2}
                                                newTab={slide.fields.openInNewTab2}
                                                linkPropertyName="slideLink2"
                                                styles={preparedStyles?.image2Styles}
                                            />
                                            <ImageSlide
                                                slide={slide}
                                                index={index}
                                                links={slideLinks3}
                                                newTab={slide.fields.openInNewTab3}
                                                linkPropertyName="slideLink3"
                                                styles={preparedStyles?.image3Styles}
                                            />
                                        </StyledWrapper>
                                        <ImageSlide
                                            slide={slide}
                                            index={index}
                                            links={slideLinks4}
                                            newTab={slide.fields.openInNewTab4}
                                            linkPropertyName="slideLink4"
                                            styles={preparedStyles?.image4Styles}
                                        />
                                    </StyledWrapper>
                                </SwiperSlide>
                            );
                        }

                        return (
                            <React.Fragment key={`slide-${index}-${slide.fields.slideTitle}`}>
                                <SwiperSlide
                                    className="mobile-slide"
                                    key={`mobile_slide_${index}_${slide.fields.imageMobile1}_1`}
                                >
                                    <ImageSlide
                                        slide={slide}
                                        index={index}
                                        links={slideLinks1}
                                        newTab={slide.fields.openInNewTab1}
                                        linkPropertyName="slideLink1"
                                        styles={preparedStyles?.image1Styles}
                                    />
                                </SwiperSlide>
                                <SwiperSlide
                                    className="mobile-slide"
                                    key={`mobile_slide_${index}_${slide.fields.imageMobile2}_2`}
                                >
                                    <ImageSlide
                                        slide={slide}
                                        index={index}
                                        links={slideLinks2}
                                        newTab={slide.fields.openInNewTab2}
                                        linkPropertyName="slideLink2"
                                        styles={preparedStyles?.image2Styles}
                                    />
                                </SwiperSlide>
                                <SwiperSlide
                                    className="mobile-slide"
                                    key={`mobile_slide_${index}_${slide.fields.imageMobile3}_3`}
                                >
                                    <ImageSlide
                                        slide={slide}
                                        index={index}
                                        links={slideLinks3}
                                        newTab={slide.fields.openInNewTab3}
                                        linkPropertyName="slideLink3"
                                        styles={preparedStyles?.image3Styles}
                                    />
                                </SwiperSlide>
                                <SwiperSlide
                                    className="mobile-slide"
                                    key={`mobile_slide_${index}_${slide.fields.imageMobile4}_4`}
                                >
                                    <ImageSlide
                                        slide={slide}
                                        index={index}
                                        links={slideLinks4}
                                        newTab={slide.fields.openInNewTab4}
                                        linkPropertyName="slideLink4"
                                        styles={preparedStyles?.image4Styles}
                                    />
                                </SwiperSlide>
                            </React.Fragment>
                        );
                    })}
                </Swiper>
                {fields.showArrows && fields.slides.length > 1 && (
                    <StyledWrapper className="swiper-navigation">
                        {/* EIS-961: changing class name here b/c we have the same name for navigation on product carousel
                            which makes both carousels share the same buttons when they are authored on the same page */}
                        <div className="swiper-button-prev-slideshow" />
                        <div className="swiper-button-next-slideshow" />
                    </StyledWrapper>
                )}
                {fields.slideIndicator && fields.slides.length > 1 && <StyledWrapper className="swiper-pagination" />}
            </>
        );
    }
}

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const definition: WidgetDefinition<CustomFieldDefinition> = {
    group: "Basic",
    icon: "presentation-screen",
    fieldDefinitions: [
        {
            name: fields.height,
            editorTemplate: "DropDownField",
            fieldType: "General" as any,
            options: [
                { displayName: "1/4 Viewport", value: "1/4 viewport" },
                { displayName: "1/2 Viewport", value: "1/2 viewport" },
                { displayName: "3/4 Viewport", value: "3/4 viewport" },
                { displayName: "Full Viewport", value: "fullViewport" },
            ],
            defaultValue: "1/4 viewport",
            hideEmptyOption: true,
            tab: settingsTab,
        },
        {
            name: fields.textAlignment,
            editorTemplate: "DropDownField",
            defaultValue: "center",
            fieldType: "General",
            options: [
                { displayName: "Left", value: "left" },
                { displayName: "Center", value: "center" },
                { displayName: "Right", value: "right" },
            ],
            hideEmptyOption: true,
            tab: settingsTab,
        },
        {
            name: fields.showArrows,
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "General",
            tab: settingsTab,
        },
        {
            name: fields.slideIndicator,
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "General",
            tab: settingsTab,
        },
        {
            name: fields.autoplay,
            editorTemplate: "IntegerField",
            defaultValue: 3,
            fieldType: "General",
            tab: settingsTab,
            min: 1,
        },
        {
            name: fields.responsiveFontSizes,
            editorTemplate: "CheckboxField",
            fieldType: "General",
            tab: settingsTab,
            defaultValue: true,
        },
        {
            name: fields.customFontSizes,
            editorTemplate: "CheckboxField",
            fieldType: "General",
            tab: settingsTab,
            defaultValue: false,
        },
        {
            name: fields.normalFontSize,
            editorTemplate: "IntegerField",
            fieldType: "General",
            tab: settingsTab,
            min: 1,
            defaultValue: null,
            isVisible: item => item?.fields[fields.customFontSizes],
        },
        {
            name: fields.h1FontSize,
            editorTemplate: "IntegerField",
            fieldType: "General",
            tab: settingsTab,
            min: 1,
            defaultValue: 40,
            isVisible: item => item?.fields[fields.customFontSizes],
        },
        {
            name: fields.h2FontSize,
            editorTemplate: "IntegerField",
            fieldType: "General",
            tab: settingsTab,
            min: 1,
            defaultValue: 32,
            isVisible: item => item?.fields[fields.customFontSizes],
        },
        {
            name: fields.h3FontSize,
            editorTemplate: "IntegerField",
            fieldType: "General",
            tab: settingsTab,
            min: 1,
            defaultValue: null,
            isVisible: item => item?.fields[fields.customFontSizes],
        },
        {
            name: fields.h4FontSize,
            editorTemplate: "IntegerField",
            fieldType: "General",
            tab: settingsTab,
            min: 1,
            defaultValue: null,
            isVisible: item => item?.fields[fields.customFontSizes],
        },
        {
            name: fields.h5FontSize,
            editorTemplate: "IntegerField",
            fieldType: "General",
            tab: settingsTab,
            min: 1,
            defaultValue: null,
            isVisible: item => item?.fields[fields.customFontSizes],
        },
        {
            name: fields.h6FontSize,
            editorTemplate: "IntegerField",
            fieldType: "General",
            tab: settingsTab,
            min: 1,
            defaultValue: null,
            isVisible: item => item?.fields[fields.customFontSizes],
        },
        {
            name: fields.upperPadding,
            editorTemplate: "IntegerField",
            fieldType: "General",
            defaultValue: 10,
            tab: settingsTab,
        },
        {
            name: fields.slides,
            editorTemplate: "ListField",
            tab: contentTab,
            getDisplay: ({ fields: { slideTitle } }) => slideTitle || "Slide",
            defaultValue: [],
            fieldType: "Translatable",
            fieldDefinitions: [
                {
                    name: "slideType",
                    displayName: "Slide Type",
                    editorTemplate: "DropDownField",
                    defaultValue: "single",
                    options: [
                        { displayName: "Single", value: "single" },
                        { displayName: "Multi-Image", value: "multi" },
                        { displayName: "Multi-Image Mirror", value: "mirror" },
                    ],
                    hideEmptyOption: true,
                    isRequired: true,
                },
                {
                    name: "slideTitle",
                    editorTemplate: "TextField",
                    defaultValue: "Slide",
                },
                {
                    name: "background",
                    displayName: "Background",
                    editorTemplate: "DropDownField",
                    options: [
                        { displayName: "Image", value: "image" },
                        { displayName: "Color", value: "color" },
                        { displayName: "Video", value: "video" },
                    ],
                    defaultValue: "image",
                    hideEmptyOption: true,
                    isVisible: widget => widget.fields.slideType === "single",
                },
                {
                    name: "videoUrl",
                    displayName: "Video Url",
                    editorTemplate: "ImagePickerField",
                    defaultValue: "",
                    isVisible: widget => widget.fields.background === "video",
                },
                {
                    name: "videoCaptionUrl",
                    displayName: "Video Caption Url",
                    editorTemplate: "ImagePickerField",
                    defaultValue: "",
                    isVisible: widget => widget.fields.background === "video",
                },
                {
                    name: "imageDesktop",
                    displayName: "Desktop Image",
                    editorTemplate: "ImagePickerField",
                    defaultValue: "",
                    isVisible: widget => widget.fields.slideType === "single" && widget.fields.background === "image",
                },
                {
                    name: "imageTablet",
                    displayName: "Tablet Image",
                    editorTemplate: "ImagePickerField",
                    defaultValue: "",
                    isVisible: widget => widget.fields.slideType === "single" && widget.fields.background === "image",
                },
                {
                    name: "imageMobile",
                    displayName: "Mobile Image",
                    editorTemplate: "ImagePickerField",
                    defaultValue: "",
                    isVisible: widget => widget.fields.slideType === "single" && widget.fields.background === "image",
                },
                {
                    name: "imageOverlay",
                    displayName: "Image Color Overlay",
                    editorTemplate: "ColorPickerField",
                    defaultValue: "",
                    isVisible: widget => widget.fields.slideType === "single" && widget.fields.background !== "color",
                },
                {
                    name: "partialOverlay",
                    editorTemplate: "CheckboxField",
                    defaultValue: false,
                    isVisible: widget => widget.fields.slideType === "single",
                },
                {
                    name: "partialOverlayPositioning",
                    editorTemplate: "DropDownField",
                    options: [
                        { displayName: "Top", value: "top" },
                        { displayName: "Middle", value: "middle" },
                        { displayName: "Bottom", value: "bottom" },
                    ],
                    hideEmptyOption: true,
                    defaultValue: "bottom",
                    isVisible: widget => widget.fields.slideType === "single" && widget.fields.partialOverlay,
                },
                {
                    name: "responsiveImageBehavior",
                    editorTemplate: "DropDownField",
                    options: [
                        { displayName: "Cover", value: "cover" },
                        { displayName: "Center", value: "center" },
                        { displayName: "Prioritize height", value: "prioritizeHeight" },
                        { displayName: "Prioritize width", value: "prioritizeWidth" },
                        { displayName: "Prioritize Height And Width", value: "prioritizeBoth" },
                    ],
                    hideEmptyOption: true,
                    defaultValue: "cover",
                    isVisible: widget => widget.fields.slideType === "single",
                },
                {
                    name: "backgroundColor",
                    editorTemplate: "ColorPickerField",
                    displayName: "Color",
                    defaultValue: "black",
                    isVisible: widget => widget.fields.slideType === "single" && widget.fields.background === "color",
                    // XNG-Changes
                    popoverLocation: "top",
                },
                {
                    name: "focalPoint",
                    displayName: "Focal Point",
                    editorTemplate: "DropDownField",
                    options: [
                        { displayName: "Top Left", value: "topLeft" },
                        { displayName: "Top Center", value: "topCenter" },
                        { displayName: "Top Right", value: "topRight" },
                        { displayName: "Center Left", value: "centerLeft" },
                        { displayName: "Center", value: "center" },
                        { displayName: "Center Right", value: "centerRight" },
                        { displayName: "Bottom Left", value: "bottomLeft" },
                        { displayName: "Bottom Center", value: "bottomCenter" },
                        { displayName: "Bottom Right", value: "bottomRight" },
                    ],
                    defaultValue: "center",
                    hideEmptyOption: true,
                    isVisible: widget => widget.fields.slideType === "single",
                },
                {
                    name: "horizontalRule",
                    defaultValue: "",
                    editorTemplate: "HorizontalRule",
                },
                {
                    name: "heading",
                    editorTemplate: "RichTextField",
                    displayName: "Heading",
                    defaultValue: "",
                    extendedConfig: { height: 100 },
                    expandedToolbarButtons: {
                        moreMisc: {},
                        code: {},
                    },
                    isVisible: widget => widget.fields.slideType === "single",
                },
                {
                    name: "subheading",
                    editorTemplate: "RichTextField",
                    displayName: "Subheading",
                    defaultValue: "",
                    extendedConfig: { height: 170 },
                    expandedToolbarButtons: {
                        moreMisc: {},
                        code: {},
                    },
                    isVisible: widget => widget.fields.slideType === "single",
                },
                {
                    name: "buttonLabel",
                    editorTemplate: "TextField",
                    displayName: "Button Label",
                    defaultValue: "",
                    isVisible: widget => widget.fields.slideType === "single",
                },
                {
                    name: "buttonLink",
                    displayName: "Button Link",
                    editorTemplate: "CustomLinkField" as any,
                    defaultValue: { type: "Page", value: "" },
                    isVisible: widget => widget.fields.slideType === "single",
                    validate: (link: LinkFieldValue, item?: HasFields) => {
                        if (!link || (!link.value && item?.fields.buttonLabel.length > 0)) {
                            return "Link is required for the button label";
                        }
                        return null;
                    },
                },
                {
                    name: "openInNewTabSingle",
                    displayName: "Open In New Tab",
                    editorTemplate: "CheckboxField",
                    defaultValue: false,
                    isVisible: widget => widget.fields.slideType === "single",
                },
                {
                    name: "buttonVariant",
                    displayName: "Button Variant",
                    editorTemplate: "DropDownField",
                    options: [
                        { displayName: "Primary", value: "primary" },
                        { displayName: "Secondary", value: "secondary" },
                        { displayName: "Tertiary", value: "tertiary" },
                    ],
                    hideEmptyOption: true,
                    defaultValue: "primary",
                    isVisible: widget => widget.fields.slideType === "single",
                },
                {
                    name: "contentPadding",
                    editorTemplate: "IntegerField",
                    defaultValue: 50,
                    isVisible: widget => widget.fields.slideType === "single",
                },
                {
                    name: "centerTextVertically",
                    editorTemplate: "CheckboxField",
                    defaultValue: false,
                },
                {
                    name: "slideLink",
                    displayName: "Slide Link",
                    editorTemplate: "CustomLinkField" as any,
                    defaultValue: { type: "Page", value: "" },
                    isVisible: widget => widget.fields.slideType === "single",
                },
                {
                    name: "openInNewTab",
                    displayName: "Open In New Tab",
                    editorTemplate: "CheckboxField",
                    defaultValue: false,
                    isVisible: widget => widget.fields.slideType === "single",
                },
                // multi and mirror fields
                {
                    name: "imageDesktop1",
                    displayName: "Desktop Image 1",
                    editorTemplate: "ImagePickerField",
                    defaultValue: "",
                    isVisible: widget => widget.fields.slideType !== "single",
                },
                {
                    name: "imageTablet1",
                    displayName: "Tablet Image 1",
                    editorTemplate: "ImagePickerField",
                    defaultValue: "",
                    isVisible: widget => widget.fields.slideType !== "single",
                },
                {
                    name: "imageMobile1",
                    displayName: "Mobile Image 1",
                    editorTemplate: "ImagePickerField",
                    defaultValue: "",
                    isVisible: widget => widget.fields.slideType !== "single",
                },
                {
                    name: "slideLink1",
                    displayName: "Slide Link 1",
                    editorTemplate: "CustomLinkField" as any,
                    defaultValue: { type: "Page", value: "" },
                    isVisible: widget => widget.fields.slideType !== "single",
                },
                {
                    name: "openInNewTab1",
                    displayName: "Open In New Tab 1",
                    editorTemplate: "CheckboxField",
                    defaultValue: false,
                    isVisible: widget => widget.fields.slideType !== "single",
                },
                {
                    name: "imageDesktop2",
                    displayName: "Desktop Image 2",
                    editorTemplate: "ImagePickerField",
                    defaultValue: "",
                    isVisible: widget => widget.fields.slideType !== "single",
                },
                {
                    name: "imageTablet2",
                    displayName: "Tablet Image 2",
                    editorTemplate: "ImagePickerField",
                    defaultValue: "",
                    isVisible: widget => widget.fields.slideType !== "single",
                },
                {
                    name: "imageMobile2",
                    displayName: "Mobile Image 2",
                    editorTemplate: "ImagePickerField",
                    defaultValue: "",
                    isVisible: widget => widget.fields.slideType !== "single",
                },
                {
                    name: "slideInnerImage",
                    displayName: "Image",
                    editorTemplate: "ImagePickerField",
                    defaultValue: "",
                },
                {
                    name: "switchImageOrder",
                    displayName: "Switch Image with text",
                    editorTemplate: "CheckboxField",
                    defaultValue: false,
                },
                {
                    name: "slideLink2",
                    displayName: "Slide Link 2",
                    editorTemplate: "CustomLinkField" as any,
                    defaultValue: { type: "Page", value: "" },
                    isVisible: widget => widget.fields.slideType !== "single",
                },
                {
                    name: "openInNewTab2",
                    displayName: "Open In New Tab 2",
                    editorTemplate: "CheckboxField",
                    defaultValue: false,
                    isVisible: widget => widget.fields.slideType !== "single",
                },
                {
                    name: "imageDesktop3",
                    displayName: "Desktop Image 3",
                    editorTemplate: "ImagePickerField",
                    defaultValue: "",
                    isVisible: widget => widget.fields.slideType !== "single",
                },
                {
                    name: "imageTablet3",
                    displayName: "Tablet Image 3",
                    editorTemplate: "ImagePickerField",
                    defaultValue: "",
                    isVisible: widget => widget.fields.slideType !== "single",
                },
                {
                    name: "imageMobile3",
                    displayName: "Mobile Image 3",
                    editorTemplate: "ImagePickerField",
                    defaultValue: "",
                    isVisible: widget => widget.fields.slideType !== "single",
                },
                {
                    name: "slideLink3",
                    displayName: "Slide Link 3",
                    editorTemplate: "CustomLinkField" as any,
                    defaultValue: { type: "Page", value: "" },
                    isVisible: widget => widget.fields.slideType !== "single",
                },
                {
                    name: "openInNewTab3",
                    displayName: "Open In New Tab 3",
                    editorTemplate: "CheckboxField",
                    defaultValue: false,
                    isVisible: widget => widget.fields.slideType !== "single",
                },
                {
                    name: "imageDesktop4",
                    displayName: "Desktop Image 4",
                    editorTemplate: "ImagePickerField",
                    defaultValue: "",
                    isVisible: widget => widget.fields.slideType !== "single",
                },
                {
                    name: "imageTablet4",
                    displayName: "Tablet Image 4",
                    editorTemplate: "ImagePickerField",
                    defaultValue: "",
                    isVisible: widget => widget.fields.slideType !== "single",
                },
                {
                    name: "imageMobile4",
                    displayName: "Mobile Image 4",
                    editorTemplate: "ImagePickerField",
                    defaultValue: "",
                    isVisible: widget => widget.fields.slideType !== "single",
                },
                {
                    name: "slideLink4",
                    displayName: "Slide Link 4",
                    editorTemplate: "CustomLinkField" as any,
                    defaultValue: { type: "Page", value: "" },
                    isVisible: widget => widget.fields.slideType !== "single",
                },
                {
                    name: "openInNewTab4",
                    displayName: "Open In New Tab 4",
                    editorTemplate: "CheckboxField",
                    defaultValue: false,
                    isVisible: widget => widget.fields.slideType !== "single",
                },
            ],
        },
    ],
};

const widgetModule = {
    component: withHistory(Slideshow),
    definition,
};

export default widgetModule;
